import { effect, Injectable } from '@angular/core';
import { HubConnectionBuilder } from '@microsoft/signalr';

import { environment } from '../../../../environments/environment';
import { patchState, signalState } from '@ngrx/signals';
import { Subject } from 'rxjs';
import {
  BookingNotifyModel,
  PrefetchBookingNotifyModel,
} from '../../models/api/notify';
import { BookingNotifyType } from '../../enums';

export interface SignalRState {
  /**
   * Mark first connection is connected
   */
  isConnected: boolean;
  /**
   * List of joined groups saved for reconnection
   */
  groups: string[];
}

@Injectable({
  providedIn: 'root',
})
export class SignalRService {
  private readonly _globalGroup = 'global';
  private _hub?: signalR.HubConnection;
  readonly state = signalState<SignalRState>({
    isConnected: false,
    groups: [this._globalGroup],
  });

  private _bookingIssue$ = new Subject<number>();
  readonly bookingIssue$ = this._bookingIssue$.asObservable();

  private _reloadBooking$ = new Subject<number>();
  readonly reloadBooking$ = this._reloadBooking$.asObservable();

  private _prefetchBooking$ = new Subject<PrefetchBookingNotifyModel>();
  readonly prefetchBooking$ = this._prefetchBooking$.asObservable();

  constructor() {
    if (environment.debug) {
      effect(() => {
        console.log('[SIGNALR_STATE]', this.state());
      });
    }
  }

  connect() {
    this._hub = new HubConnectionBuilder()
      .withUrl(`${environment.notificationApiUrl}/notify`)
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          if (
            retryContext.elapsedMilliseconds <
            environment.defaultRetryConnectSignalMinutes * 60 * 1000
          ) {
            switch (retryContext.previousRetryCount) {
              case 0:
                return 0;
              case 1:
                return 2000;
              case 2:
                return 10000;
              default:
                return 30000;
            }
          } else {
            return null;
          }
        },
      })
      .configureLogging(environment.signalRLogLevel)
      .build();

    this._hub
      .start()
      .then(() => {
        this.initListener();
        this.join();
        patchState(this.state, (_) => ({
          isConnected: true,
        }));
      })
      .catch((err) => {
        patchState(this.state, (_) => ({
          isConnected: false,
        }));
        console.error('Error while starting connection: ' + err);
      });
  }

  disconnect(): void {
    this._hub?.stop().then(() => {
      patchState(this.state, (_) => ({
        isConnected: false,
      }));
      console.warn('Connection disconnected!');
    });
  }

  private async join() {
    for (const group of this.state.groups()) {
      await this.joinGroup(group);
    }
  }

  //#region Hub Methods
  async joinGroup(groupName: string) {
    try {
      if (this.state.groups().indexOf(groupName) === -1) {
        if (environment.debug) {
          console.log('[signalR -> joinGroup]', groupName);
        }
        await this._hub?.invoke('JoinGroup', groupName);
        patchState(this.state, (state) => ({
          groups: [...state.groups, groupName],
        }));
      }
    } catch (error) {
      console.error(error);
    }
  }

  async leaveGroup(groupName: string) {
    try {
      await this._hub?.invoke('LeaveGroup', groupName);
      patchState(this.state, (state) => ({
        groups: state.groups.filter((g) => g !== groupName),
      }));
    } catch (error) {
      console.error(error);
    }
  }
  //#endregion

  //#region Private Methods
  private initListener() {
    this.listenConnectState();
    this.receiveNotifyBooking();
    this.receiveNotifyBookingIssue();
    this.receiveNotifyReloadBooking();
  }

  private listenConnectState() {
    this._hub?.onreconnected(() => {
      this.join();
    });
  }

  private receiveNotifyBookingIssue() {
    this._hub?.on('ReceiveNotifyBookingIssue', (bookingId: number) => {
      if (environment.debug) {
        console.log('[signalR -> receiveNotifyBookingIssue]', bookingId);
      }
      this.playAudio();
      this._bookingIssue$.next(bookingId);
    });
  }

  private receiveNotifyReloadBooking() {
    this._hub?.on('ReceiveNotifyReloadBooking', (bookingId: number) => {
      if (environment.debug) {
        console.log('[signalR -> receiveNotifyReloadBooking]', bookingId);
      }
      this._reloadBooking$.next(bookingId);
    });
  }

  private receiveNotifyBooking() {
    this._hub?.on('ReceiveNotifyBooking', (data: BookingNotifyModel) => {
      if (environment.debug) {
        console.log('[signalR -> receiveNotifyBooking]', {
          ...data,
          Data: JSON.parse(data.Data),
        });
      }

      switch (data.NotifyType) {
        case BookingNotifyType.Issue:
          this.handleNotifyBookingIssue(data.BookingId);
          break;

        case BookingNotifyType.Reload:
          this.handleNotifyReloadBooking(data.BookingId);
          break;

        case BookingNotifyType.Prefetch:
          this.handleNotifyPrefetchBooking(
            data.BookingId,
            JSON.parse(data.Data),
          );
          break;
      }
    });
  }

  private handleNotifyBookingIssue(bookingId: number) {
    this.playAudio();
    this._bookingIssue$.next(bookingId);
  }

  private handleNotifyReloadBooking(bookingId: number) {
    this._reloadBooking$.next(bookingId);
  }

  private handleNotifyPrefetchBooking(
    bookingId: number,
    data: PrefetchBookingNotifyModel['data'],
  ) {
    this._prefetchBooking$.next({
      bookingId,
      data,
    });
  }

  private playAudio() {
    const audio = new Audio();
    audio.src = 'sounds/notification.mp3';
    audio.load();
    audio.currentTime = 0.1;
    audio.play();
  }
  //#endregion
}
